<ng-container *ngIf="policy$ | async as policy">
  <ng-container *ngIf="plan$ | async as plan">
    <div
      *vsLet="canEditPlan$ | async as canEditPlans"
      class="min-w-[77%] bg-base-white flex-[10] w-full shadow-lg p-4 h-[100%] min-h-[75vh] rounded-b-lg rounded-tr-lg"
      [class.rounded-tl-lg]="roundedCorner$ | async"
    >
      <ng-container *ngIf="propertySet$ | async as propertySet">
        <div class="flex flex-col w-full">
          <vs-tabs theme="primary" class="mb-2" (tabChanged)="activeTabIndex = $event">
            <div *ngIf="activeTabIndex === 0" slot="after-tabs" class="flex gap-2 items-center">
              <button
                *ngIf="canEditPlans"
                vs-icon-button
                size="md"
                appearance="clear"
                [disabled]="saving"
                (click)="openEditPlanDialog()"
              >
                <vs-icon name="edit-01"></vs-icon>
              </button>

              <button
                *ngIf="canCreatePlan$ | async"
                vs-icon-button
                size="md"
                appearance="clear"
                tooltipStyle="light"
                tooltipPosition="top"
                vsTooltip="Duplicate this plan"
                [disabled]="saving"
                (click)="openDuplicatePlanDialog()"
              >
                <vs-icon name="copy-04"></vs-icon>
              </button>

              <button
                *ngIf="canDeletePlan$ | async"
                vs-icon-button
                size="md"
                appearance="clear"
                (click)="openDeletePlanDialog()"
              >
                <vs-icon name="trash-01"></vs-icon>
              </button>
            </div>

            <vs-tab [config]="{ label: 'Details' }">
              <div class="flex flex-col gap-4 my-4 mx-2">
                <div class="flex gap-2">
                  <p class="text-sm font-medium text-gray-700 basis-1/5">Title</p>
                  <p class="text-sm text-gray-900">{{ plan.title }}</p>
                </div>

                <div class="flex sm:flex-row flex-col gap-2">
                  <p class="text-sm font-medium text-gray-700 basis-1/5">Description</p>
                  <p class="text-sm text-gray-900 overflow-y-auto max-h-[80px] basis-4/5 scrollbar-thin">
                    {{ plan.description }}
                  </p>
                </div>

                <div class="flex sm:flex-row flex-col gap-2">
                  <p class="text-sm font-medium text-gray-700 basis-1/5">Plan Liability Limit</p>
                  <div class="text-sm text-gray-900">
                    <vs-badge
                      *ngIf="plan.planLiabilityLimitPropertyPath"
                      theme="default"
                      [vsTooltip]="plan.planLiabilityLimitPropertyPath"
                      tooltipPosition="top"
                    >
                      {{ plan.planLiabilityLimitPropertyPath | valuePathLabel: propertySet | async }}
                    </vs-badge>
                    <span *ngIf="!plan.planLiabilityLimitPropertyPath">
                      {{ (plan.planLiabilityLimit ?? 0) / 100 | currency }}
                    </span>
                  </div>
                </div>

                <div class="flex sm:flex-row flex-col gap-2">
                  <p class="text-sm font-medium text-gray-700 basis-1/5">Order</p>
                  <p class="text-sm text-gray-900">{{ plan.order }}</p>
                </div>

                <div class="flex sm:flex-row flex-col gap-2">
                  <p class="text-sm font-medium text-gray-700 basis-1/5">Conditions</p>

                  <ws-condition-preview
                    class="text-sm text-gray-900"
                    [groupValue]="plan.conditions"
                    [propertySet]="propertySet"
                  ></ws-condition-preview>
                </div>

                <div class="flex sm:flex-row flex-col gap-2">
                  <p class="text-sm font-medium text-gray-700 basis-1/5">{{ usageLimitConfig.termSettingsLabel }}</p>

                  <div class="basis-4/5 flex flex-col gap-3">
                    <!-- Off for the whole policy, so said once instead of in every term -->
                    <div
                      *ngIf="(newClaimsFlow$ | async) === true && !policy.checkClaimEligibility"
                      class="flex gap-2 text-sm"
                      data-testid="term-usage-limits-off"
                    >
                      <p class="text-gray-700 font-medium">{{ usageLimitConfig.off.label }}</p>
                      <p class="text-gray-600">{{ usageLimitConfig.off.summary }}</p>
                    </div>

                    <div *ngFor="let term of policy.terms" class="border border-gray-200 rounded-md p-3">
                      <div class="flex items-start justify-between gap-2 mb-2">
                        <div>
                          <p class="text-sm font-medium text-gray-900">{{ term.title }}</p>
                        </div>

                        <button
                          *ngIf="canEditPlans"
                          vs-button
                          size="sm"
                          appearance="outline"
                          (click)="openEditTermConditionsDialog(term)"
                        >
                          Edit
                        </button>
                      </div>

                      <ws-condition-preview
                        class="text-sm text-gray-900"
                        [groupValue]="plan.termConditions?.[term.id] ?? null"
                        [propertySet]="propertySet"
                      ></ws-condition-preview>

                      <!-- Per term while the check is on; a limit saved earlier stays visible when it's off -->
                      <div
                        *ngIf="
                          (newClaimsFlow$ | async) === true &&
                          (policy.checkClaimEligibility || plan.termUsageLimits?.[term.id])
                        "
                        class="flex gap-2 mt-2 text-sm"
                        data-testid="term-usage-limit"
                      >
                        <p class="text-gray-700 font-medium">{{ usageLimitConfig.detailsLabel }}</p>
                        <p
                          *ngIf="plan.termUsageLimits?.[term.id] as usageLimit; else noUsageLimit"
                          class="text-gray-900"
                        >
                          <ng-container *ngIf="usageLimit.limit.valuePropertyPath; else fixedUsageLimit">
                            {{
                              usageLimitConfig.summaryFromAsset(
                                (usageLimit.limit.valuePropertyPath | valuePathLabel: propertySet | async) ?? '',
                                dimensionTitle(propertySet, usageLimit.dimensionId)
                              )
                            }}
                          </ng-container>
                          <ng-template #fixedUsageLimit>
                            {{
                              usageLimitConfig.summary(
                                (usageLimit.limit.defaultValue | number) ?? '',
                                dimensionTitle(propertySet, usageLimit.dimensionId)
                              )
                            }}
                          </ng-template>
                          <span *ngIf="usageLimit.limit.blocks?.length" class="text-gray-600">
                            · {{ usageLimitConfig.conditional(usageLimit.limit.blocks.length) }}
                          </span>
                          <span *ngIf="!policy.checkClaimEligibility" class="text-gray-600">
                            · {{ usageLimitConfig.off.notChecked }}
                          </span>
                        </p>
                        <ng-template #noUsageLimit>
                          <p class="text-gray-600">{{ usageLimitConfig.none }}</p>
                        </ng-template>
                      </div>
                    </div>
                  </div>
                </div>

                <form *vsLet="changeVisibility$ | async" class="flex" [formGroup]="visibilityForm">
                  <div
                    *vsLet="canEditVisibility$ | async as canEditVisibility"
                    class="flex gap-4"
                    tooltipStyle="light"
                    tooltipPosition="end"
                    [vsTooltip]="!canEditVisibility ? 'Insufficient permissions to toggle visibility' : ''"
                  >
                    <input
                      vsInput
                      type="checkbox"
                      theme="primary"
                      appearance="toggle"
                      formControlName="visible"
                      [class.no-pointer-class]="canEditVisibility"
                    />

                    <div class="flex flex-col gap-1">
                      <p class="text-sm font-medium text-gray-700">Plan Visibility</p>
                      <p class="text-sm text-gray-600">
                        To hide this plan from customers at checkout, toggle its visibility settings by editing the plan
                        details.
                      </p>
                    </div>
                  </div>
                </form>
              </div>
              <ws-plan-pricing-table
                [policy]="policy"
                [plan]="plan"
                [propertySet]="propertySet"
                [canEditPlans]="!!canEditPlans"
                [dynamicRatingEnabled]="dynamicRatingEnabled$ | async"
              ></ws-plan-pricing-table>
            </vs-tab>

            <vs-tab *ngIf="dynamicRatingEnabled$ | async" [config]="{ label: 'Rating Matrix' }">
              <ws-policy-rating-matrix-page></ws-policy-rating-matrix-page>
            </vs-tab>
          </vs-tabs>
        </div>
      </ng-container>
    </div>

    <ws-plan-dialog *ngIf="policy" #editPlanDialog [policy]="policy" [plan]="plan"></ws-plan-dialog>

    <ws-plan-term-conditions-dialog
      *ngIf="policy && plan"
      #editTermConditionsDialog
      [policy]="policy"
      [plan]="plan"
    ></ws-plan-term-conditions-dialog>

    <ws-delete-plan-dialog
      *ngIf="policy && plan"
      #deletePlanDialog
      [plan]="plan"
      [policy]="policy"
    ></ws-delete-plan-dialog>

    <ws-duplicate-plan-dialog
      *ngIf="policy && plan"
      #duplicatePlanDialog
      [plan]="plan"
      [policy]="policy"
    ></ws-duplicate-plan-dialog>
  </ng-container>
</ng-container>
